import React, { useCallback, useMemo, useState } from 'react';
import Alert from '@uber/base-icons/alert';
import DiamondEmpty from '@uber/base-icons/diamond-empty';
import Hide from '@uber/base-icons/hide';
import Hourglass from '@uber/base-icons/hourglass';
import Lock from '@uber/base-icons/lock';
import { useStyletron } from 'baseui';
import { Banner, KIND } from 'baseui/banner';
import { useSnackbar } from 'baseui/snackbar';
import { KIND as TagKind } from 'baseui/tag';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { ACTION_BY } from '../../../../common/constant/constant';
import { REVIEW_ACTION_TYPE } from '../../../../common/review-action/constants/index';
import useAnalyticsForReviewSidePanel from '../../../../common/review-mode-side-panel/hooks/useAnalyticsForReviewSidePanel';
import type { QuestionnairePayloadT } from '../../../../common/side-panel/analytics/types';
import useAnalyticsForSidePanel from '../../../../common/side-panel/analytics/useAnalyticsForSidePanel';
import DynamicQuestionnaire from '../../../../common/side-popover-panel/dynamic-questionnaire/dynamic-questionnaire';
import useDynamicQuestionnaire from '../../../../common/side-popover-panel/dynamic-questionnaire/useDynamicQuestionnaire';
import { TASK_MODE } from '../../../../common/task-state/constants';
import type { ConfigT } from '../../../../common/types/types';
import { isQuestionnaireUpdated } from '../../../../common/utils/utils';
import { type ModernizedGenericAnnotation, type SidePanelDispatchAtom, EditActionSection, LabelModificationSelector, LabelQuestionnaire, StatusList } from '../../../../modernization-common/side-panel';
import { LONG_TOAST_DURATION, SHORT_TOAST_DURATION } from '../../../../modernization-common/side-panel/const';
import { SIDE_PANEL_ACTIONS } from '../../../../modernization-common/side-panel/const';
import { LABEL_TYPE_CONFIG, TOOL_CONFIG } from '../../../labelling/components/toolbar/constants';
import { filterDynamicAnswers as excludeDynamicAnswers, filterStaticAnswers as excludeStaticAnswers } from '../../../labelling/reducers/utils/utils';
import type { AnnotationT } from '../../../labelling/types';
import { annotationLabelsAtom, annotationMetadataAtom, annotationUIStateAtom, isVideoTaskAtom } from '../../atoms/annotation-atoms';
import { instanceToRuleMap } from '../../atoms/qa-engine-atom';
import { annotationIdToSpanAtom } from '../../atoms/side-panel-atoms';
import { taskModeAtom } from '../../atoms/task-mode-atoms';
import { selectedAnnotationIdsAtom } from '../../atoms/tool-atoms';
import { useRelationshipsEnabled } from '../../hooks/use-relationships-enabled';
import { ViewRelationships } from '../relationship-component/view-relationships';
import { useDynamicQuestionnaireDetection } from './hooks/use-dynamic-questionnaire-detection';
import { useEditAnnotationForm } from './hooks/use-edit-annotation-form';
import { useRelationshipHandlers } from './hooks/use-relationship-handlers';
import type { SelectedAnnotationDetailsT } from './types';
const COMBINED_TOOL_CONFIG = {
  ...TOOL_CONFIG,
  ...LABEL_TYPE_CONFIG
};
const SWITCH_LABEL_ADDITIONAL_INFO = 'Switching to this label would clear the current answers';
const getBannerOverride = () => ({
  Root: {
    style: () => ({
      margin: 0
    })
  }
});
type AttributeTag = {
  label: string;
  kind: keyof typeof TagKind;
  startEnhancer?: React.ReactNode;
};
type EditAnnotationsPanelProps = {
  onClose?: () => void;
  dispatchAtom: SidePanelDispatchAtom<AnnotationT & ModernizedGenericAnnotation>;
  onValidateAnswers?: (annotationId: string, answers: any) => Promise<any[]>;
  config?: ConfigT;
};
const ATTRIBUTE_TAGS: Record<string, AttributeTag> = {
  PENDING: {
    label: 'PENDING',
    kind: TagKind.accent,
    startEnhancer: <Hourglass size={14} />
  },
  LOCKED: {
    label: 'LOCKED',
    kind: TagKind.warning,
    startEnhancer: <Lock size={14} />
  },
  HIDDEN: {
    label: 'HIDDEN',
    kind: TagKind.warning,
    startEnhancer: <Hide size={14} />
  },
  RANGE: {
    label: 'Range',
    kind: TagKind.accent
  }
};
const selectedAnnotationDetailsAtom = atom<SelectedAnnotationDetailsT | undefined>(get => {
  const selectedAnnotationId = get(selectedAnnotationIdsAtom)[0];
  if (!selectedAnnotationId) {
    return undefined;
  }
  const metadata = get(annotationMetadataAtom).get(selectedAnnotationId);
  if (!metadata) {
    return undefined;
  }
  const annotationLabel = get(annotationLabelsAtom).find(l => l.title === metadata.labelId) ?? {
    title: metadata.labelId,
    style: {
      strokeColor: '',
      fillColor: '',
      fillOpacity: 0,
      strokeWidth: 0
    }
  };
  const uiState = get(annotationUIStateAtom).get(selectedAnnotationId);
  return {
    id: selectedAnnotationId,
    annotationType: metadata.annotationType,
    annotationLabel,
    metadata: {
      answers: metadata.answers
    },
    locked: uiState?.locked ?? false,
    hidden: uiState?.hidden ?? false
  };
});
const attributeListAtom = atom(get => {
  const attributeList: AttributeTag[] = [];
  const selectedAnnotationDetails = get(selectedAnnotationDetailsAtom);
  if (!selectedAnnotationDetails) {
    return [];
  }
  const hasQuestionnaire = (selectedAnnotationDetails.annotationLabel?.questionnaire?.length ?? 0) > 0;
  const isPending = hasQuestionnaire && !Object.keys(selectedAnnotationDetails.metadata?.answers || {}).length;
  attributeList.push(
  // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'AttributeTag | undefined' is not assignable to parameter of type 'AttributeTag'.
  ...(isPending ? [ATTRIBUTE_TAGS.PENDING] : []), ...(selectedAnnotationDetails?.locked ? [ATTRIBUTE_TAGS.LOCKED] : []), ...(selectedAnnotationDetails?.hidden ? [ATTRIBUTE_TAGS.HIDDEN] : []));
  const isVideoTask = get(isVideoTaskAtom);
  if (!isVideoTask) {
    return attributeList;
  }
  const annotationIdToSpan = get(annotationIdToSpanAtom);
  const rangeSpans = annotationIdToSpan[selectedAnnotationDetails.id];
  const frameAttributes =
  // @ts-expect-error todo(ts-migration) TS18048 'rangeSpans' is possibly 'undefined'.
  rangeSpans.length > 0 ? [{
    ...ATTRIBUTE_TAGS.RANGE,
    // @ts-expect-error todo(ts-migration) TS18048 'ATTRIBUTE_TAGS.RANGE' is possibly 'undefined'.
    label: `${ATTRIBUTE_TAGS.RANGE.label}: ${rangeSpans.map(span => {
      if (span.start === span.end) {
        return `${span.start + 1}`;
      }
      return `${span.start + 1} -> ${span.end + 1}`;
    }).join(', ')}`
  }] : [];
  // @ts-expect-error todo(ts-migration) TS2769 No overload matches this call.
  return attributeList.concat(frameAttributes);
});
export const EditAnnotationsPanel = ({
  onClose,
  dispatchAtom,
  onValidateAnswers,
  config
}: EditAnnotationsPanelProps) => {
  const [isValidationLoading, setIsValidationLoading] = useState(false);
  const isRelationshipsEnabled = useRelationshipsEnabled();
  const {
    createRelationship,
    editRelationship,
    deleteRelationship
  } = useRelationshipHandlers();
  const [css, theme] = useStyletron();
  const {
    enqueue
  } = useSnackbar();
  const dispatch = useSetAtom(dispatchAtom);
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const selectedAnnotationDetails = useAtomValue(selectedAnnotationDetailsAtom);
  const isVideoTask = useAtomValue(isVideoTaskAtom);
  const attributeList = useAtomValue(attributeListAtom);
  const annotationIdToSpan = useAtomValue(annotationIdToSpanAtom);
  const qaErrorMap = useAtomValue(instanceToRuleMap);
  const qaErrorSelectedAnnotation = selectedAnnotationDetails?.id ? qaErrorMap[selectedAnnotationDetails.id] : undefined;
  const {
    selectedLabel,
    questionnaireAnswers,
    isAnswerValid,
    hasQuestionnaire,
    compatibleLabels,
    handleLabelChange,
    handleQuestionnaireChange,
    getSelectedLabelQuestionnaireId
  } = useEditAnnotationForm({
    selectedAnnotationDetails,
    annotationLabels
  });
  const questionnaireId = getSelectedLabelQuestionnaireId();
  const {
    hasDynamicQuestionnaire,
    getSelectedAnnotationFrameRange,
    handleResetDynamicFields,
    updateDynamicAnnotation,
    getDynamicAnswersRange,
    validateDynamicAnnotation
  } = useDynamicQuestionnaireDetection({
    questionnaireId,
    selectedLabel,
    annotationIdToSpan,
    dispatch
  });
  const dynamicQuestionConfig = useDynamicQuestionnaire({
    getDynamicAnswersRange: getDynamicAnswersRange,
    selectedAnnotationId: selectedAnnotationDetails?.id || '',
    annotation: selectedAnnotationDetails!,
    resetDynamicFields: handleResetDynamicFields,
    getSelectedAnnotationFrameRange,
    validateDynamicAnnotation,
    showNotification: (_, message) => {
      enqueue({
        message,
        startEnhancer: () => <Alert title="Error" />
      }, LONG_TOAST_DURATION);
    },
    updateDynamicAnnotation,
    editableAnnotationId: selectedAnnotationDetails?.id || ''
  });
  const {
    sendEventForQuestionnaireSkipped,
    sendEventForUpdateAnnotationLabel,
    sendEventForQuestionnaireAnswered,
    sendEventForQuestionnaireUpdated
  } = useAnalyticsForSidePanel();
  const taskMode = useAtomValue(taskModeAtom);
  const isReviewMode = taskMode === TASK_MODE.REVIEW;
  const {
    sendEventUpdateAnnotationReview
  } = useAnalyticsForReviewSidePanel();
  const options = useMemo(() => {
    return compatibleLabels.map(label => {
      const labelTool = label.tools?.[0];
      const toolDetails = COMBINED_TOOL_CONFIG[labelTool || ''];
      const Icon = toolDetails?.Icon;
      const isNotCurrentLabel = selectedLabel !== label.title;
      return {
        label: label.title,
        value: label.title,
        additionalInfo: hasQuestionnaire && isNotCurrentLabel ? SWITCH_LABEL_ADDITIONAL_INFO : undefined,
        startEnhancer: Icon ? <Icon strokeColor={label.style.strokeColor} size={14} /> : <DiamondEmpty color={label.style.strokeColor} size={14} />
      };
    });
  }, [compatibleLabels, selectedLabel, hasQuestionnaire]);
  const getQuestionnaireEventPayload = useCallback((annotation: SelectedAnnotationDetailsT, annotationId: string): QuestionnairePayloadT => {
    return {
      objectId: annotationId,
      objectType: annotation.annotationType,
      label: annotation.annotationLabel.title
    };
  }, []);
  const handleClose = () => {
    if (!selectedAnnotationDetails) {
      return;
    }
    const questionnaireEventPayload = getQuestionnaireEventPayload(selectedAnnotationDetails, selectedAnnotationDetails.id);
    if (!selectedAnnotationDetails.metadata?.answers) sendEventForQuestionnaireSkipped(questionnaireEventPayload);
    onClose?.();
  };
  const onSave = async () => {
    if (!selectedAnnotationDetails) {
      return;
    }
    if (selectedAnnotationDetails.locked || selectedAnnotationDetails.hidden) {
      enqueue({
        message: `Annotation is ${selectedAnnotationDetails.locked ? 'locked' : 'hidden'} and can't be edited.`,
        startEnhancer: () => selectedAnnotationDetails.locked ? <Lock title="Locked" /> : <Hide title="Hidden" />
      }, SHORT_TOAST_DURATION);
      return;
    }
    if (hasQuestionnaire) {
      if (!questionnaireAnswers || !isAnswerValid) {
        enqueue({
          message: 'Please complete all required fields.',
          startEnhancer: () => <Alert title="Error" />
        }, LONG_TOAST_DURATION);
        return;
      }
      if (questionnaireAnswers && onValidateAnswers) {
        setIsValidationLoading(true);
        try {
          const validationResults = await onValidateAnswers(selectedAnnotationDetails.id, questionnaireAnswers);
          if (validationResults.length > 0) {
            return;
          }
        } catch {
          // TODO: handle error with logger
        } finally {
          setIsValidationLoading(false);
        }
      }
    }
    const {
      id,
      ...rest
    } = selectedAnnotationDetails;
    const isLabelUpdated = selectedAnnotationDetails.annotationLabel.title !== selectedLabel;
    const newLabel = annotationLabels.find(label => label.title === selectedLabel);
    if (isLabelUpdated && newLabel) {
      dispatch({
        type: SIDE_PANEL_ACTIONS.UPDATE_ANNOTATION_LABEL,
        annotationIdx: id,
        annotationLabel: newLabel
      });
    }
    const updatedStaticAnswers = excludeDynamicAnswers(questionnaireAnswers);
    const existingDynamicAnswers = excludeStaticAnswers(rest.metadata?.answers);
    const mergedAnswers = {
      ...updatedStaticAnswers,
      ...existingDynamicAnswers
    };
    dispatch({
      type: SIDE_PANEL_ACTIONS.UPDATE_ANNOTATION_QUESTIONNAIRE,
      annotationIdx: id,
      answers: mergedAnswers
    });
    const updatedAnnotation = {
      ...rest,
      id,
      annotationLabel: newLabel || rest.annotationLabel,
      metadata: {
        ...rest.metadata,
        answers: mergedAnswers
      }
    };
    const hasAnswers = updatedAnnotation.metadata?.answers && Object.keys(updatedAnnotation.metadata?.answers).length > 0;
    if (hasAnswers) {
      const questionnaireUpdated = isQuestionnaireUpdated(selectedAnnotationDetails, updatedAnnotation);
      const questionnaireEventPayload = getQuestionnaireEventPayload(updatedAnnotation, id);
      questionnaireUpdated ? sendEventForQuestionnaireUpdated(questionnaireEventPayload) : !selectedAnnotationDetails.metadata?.answers && sendEventForQuestionnaireAnswered(questionnaireEventPayload);
    }
    if (selectedAnnotationDetails.annotationLabel.title !== updatedAnnotation.annotationLabel.title) {
      sendEventForUpdateAnnotationLabel({
        objectType: updatedAnnotation.annotationType,
        prevLabel: selectedAnnotationDetails.annotationLabel.title,
        newLabel: updatedAnnotation.annotationLabel.title,
        objectId: id,
        actionBy: ACTION_BY.EDIT
      });
    }
    if (isReviewMode) {
      sendEventUpdateAnnotationReview({
        objectId: id,
        objectType: updatedAnnotation.annotationType,
        label: updatedAnnotation.annotationLabel.title,
        status: REVIEW_ACTION_TYPE.PENDING
      });
    }
    requestAnimationFrame(() => {
      // this is to ensure that the drawer is closed after the annotation is saved
      onClose?.();
    });
  };
  if (!selectedAnnotationDetails) {
    onClose?.();
    return null;
  }
  return <div className={css({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  })}>
      <section className={css({
      overflow: 'auto',
      flexBasis: '85%',
      paddingBottom: theme.sizing.scale1600
    })}>
        <StatusList list={attributeList} />
        <LabelModificationSelector options={options} currentValue={selectedLabel || ''} onChange={handleLabelChange} header="Class" />
        {qaErrorSelectedAnnotation && <Banner title="QA checks failed" kind={KIND.negative} overrides={getBannerOverride()}>
            {qaErrorSelectedAnnotation[0] && <p key={qaErrorSelectedAnnotation[0].ruleName}>
                {qaErrorSelectedAnnotation[0].errorMessage}
              </p>}
          </Banner>}
        {hasQuestionnaire && <LabelQuestionnaire key={`${getSelectedLabelQuestionnaireId()}:${selectedLabel}`} header="Object properties" questionnaireId={getSelectedLabelQuestionnaireId()} answers={questionnaireAnswers || {}} onChange={handleQuestionnaireChange} isQuestionnaireComposable={config?.useComposableQuestionnaire} />}
        {selectedAnnotationDetails && hasDynamicQuestionnaire && <DynamicQuestionnaire annotation={selectedAnnotationDetails} isQuestionnaireComposable={config?.useComposableQuestionnaire ?? false} createNewDynamicQuestionnaire={dynamicQuestionConfig.createNewDynamicQuestionnaire} dynamicAnnotationConfig={dynamicQuestionConfig.dynamicAnnotationConfig} editableAnnotationId={selectedAnnotationDetails.id} handleOnDynamicQuestionnaireChange={dynamicQuestionConfig.handleOnDynamicQuestionnaireChange} updateFrameStart={dynamicQuestionConfig.updateFrameStart} updateFrameEnd={dynamicQuestionConfig.updateFrameEnd} onRemoveDynamicAttribute={dynamicQuestionConfig.onRemoveDynamicAttribute} onSaveDynamicAttribute={dynamicQuestionConfig.onSaveDynamicAttribute} hasDynamicQuestionnaire={hasDynamicQuestionnaire} />}
        {isRelationshipsEnabled && <ViewRelationships onCreateRelationship={createRelationship} onEditRelationship={editRelationship} onDeleteRelationship={deleteRelationship} isVideoTask={isVideoTask} />}
      </section>
      <EditActionSection onPrimaryAction={onSave} onSecondaryAction={onClose ? handleClose : undefined} primaryActionLabel="Save" secondaryActionLabel="Cancel" isPrimaryLoading={isValidationLoading} isPrimaryDisabled={isValidationLoading} />
    </div>;
};